import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import CategoryBadge from "../../components/doctors/CategoryBadge";
import FeedbackBadge from "../../components/visits/FeedbackBadge";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Phone,
  Target,
  UserCheck,
  Pencil,
  Power,
  History,
  Loader2,
  Inbox,
  Clock,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";
import { isPendingMasterChange, sentForApprovalMessage } from "../../lib/masterChange";
import TerritoryLabel from "../../components/common/TerritoryLabel";
import { formatISTDate, formatISTTime } from "../../lib/datetime";
import { joinParts } from "../../lib/vocabulary";

export default function DoctorDetailPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visitsError, setVisitsError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deactivating, setDeactivating] = useState(false);

  const canEdit =
    user?.role === "director" ||
    user?.role === "admin" ||
    user?.role === "assistant_manager" ||
    user?.role === "admin_manager";
  const canDeactivate = user?.role === "director" || user?.role === "admin";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/doctors/${id}`)
      .then(({ data }) => {
        if (!cancelled) setDoctor(data);
      })
      .catch((e) => {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      })
      .finally(() => !cancelled && setLoading(false));
    // Visit history. PATTERN A: this used to `.catch(() => {})`, so a failed
    // load left `visits` empty and rendered "No visits logged yet" — a load
    // failure presented as a fact about the doctor. A failure is a failure.
    setVisitsError("");
    api
      .get(`/doctor-visits`, { params: { doctor_id: id, limit: 10 } })
      .then(({ data }) => !cancelled && setVisits(data))
      .catch((e) => {
        if (!cancelled) {
          setVisits([]);
          setVisitsError(
            formatApiErrorDetail(e.response?.data?.detail) || e.message || "Request failed"
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function onDeactivate() {
    setDeactivating(true);
    try {
      const { data } = await api.delete(`/doctors/${id}`);
      toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "Doctor deactivated");
      navigate("/doctors");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDeactivating(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-callisto-muted text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading doctor…
      </div>
    );
  }

  if (error || !doctor) {
    return (
      <div className="bg-white border border-callisto rounded-xl p-8 text-center">
        <h2 className="text-lg font-bold text-callisto-navy">Doctor unavailable</h2>
        <p className="mt-2 text-sm text-callisto-muted">{error || "Not found"}</p>
        <Link
          to="/doctors"
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-callisto-gold hover:underline"
        >
          <ArrowLeft size={14} /> Back to doctors
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="doctor-detail-page">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link
          to="/doctors"
          className="inline-flex items-center gap-1 text-sm text-callisto-muted hover:text-callisto-navy"
        >
          <ArrowLeft size={14} />
          Back to doctors
        </Link>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => navigate(`/doctors/${id}/edit`)}
              className="h-10 border-callisto"
              data-testid="doctor-edit-button"
            >
              <Pencil className="h-4 w-4 mr-1.5" />
              Edit
            </Button>
          )}
          {canDeactivate && doctor.is_active && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 border-red-200 text-red-700 hover:bg-red-50"
                  data-testid="doctor-deactivate-button"
                >
                  <Power className="h-4 w-4 mr-1.5" />
                  Deactivate
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Deactivate this doctor?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {doctor.name} will be marked inactive and hidden from the
                    Sales Officer view. Their visit history will be retained
                    and they can be reactivated later by an admin.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="doctor-deactivate-cancel">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDeactivate}
                    disabled={deactivating}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="doctor-deactivate-confirm"
                  >
                    {deactivating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Deactivate"
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Hero card */}
      <div
        className={`bg-white border rounded-xl p-5 md:p-7 ${
          doctor.category === "A"
            ? "border-callisto-gold/40 shadow-[0_0_0_1px_rgba(201,168,76,0.05)]"
            : "border-callisto"
        }`}
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-14 w-14 rounded-xl bg-callisto-navy text-callisto-gold flex items-center justify-center text-lg font-bold shrink-0">
            {initials(doctor.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-callisto-navy">
                {doctor.name}
              </h1>
              <CategoryBadge category={doctor.category} size="lg" />
              {!doctor.is_active && (
                <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                  Inactive
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-callisto-body">
              {joinParts([doctor.specialization, doctor.qualification])}
            </p>
            <p className="mt-1 text-sm text-callisto-muted flex items-center gap-1.5">
              <Building2 size={14} className="text-callisto-navy/60" />
              {doctor.clinic_hospital_name}
            </p>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-callisto rounded-xl p-5 md:p-7">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-4">
            Profile
          </p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <Field icon={MapPin} label="Address">
              {doctor.address}
            </Field>
            <Field icon={MapPin} label="Territory unit · City · Pin">
              {/* RC-10: this was `{unit || area}, {city} — {pin}`, which read
                  ", Patna — " when any part was missing, and showed an
                  unmigrated free-text territory as though it were a unit. */}
              <TerritoryLabel
                value={doctor.territory_unit || doctor.area}
                unmigrated={doctor.territory_unmigrated}
                suffixes={[doctor.city, doctor.pin_code]}
                testId="doctor-territory"
              />
            </Field>
            <Field icon={Phone} label="Mobile">
              {doctor.mobile || <span className="text-callisto-muted">Not provided</span>}
            </Field>
            <Field icon={Target} label="Monthly visit target">
              {doctor.monthly_visit_target == null ? (
                <span className="text-callisto-muted"
                      data-testid="visit-target-unset">
                  Not set — no company rule for category {doctor.category || "—"}
                </span>
              ) : (
                <>
                  <span className="font-bold text-callisto-navy">
                    {doctor.monthly_visit_target}
                  </span>{" "}
                  <span className="text-callisto-muted">
                    visits / month
                    {doctor.monthly_visit_target_source === "setting"
                      ? " (company rule for this category)"
                      : ""}
                  </span>
                </>
              )}
            </Field>
            <Field icon={UserCheck} label="Assigned Sales Officer">
              {doctor.assigned_to_user ? (
                <span>
                  <span className="font-semibold text-callisto-navy">
                    {doctor.assigned_to_user.name}
                  </span>
                  <span className="block text-xs text-callisto-muted">
                    {doctor.assigned_to_user.email}
                  </span>
                </span>
              ) : (
                <span className="text-callisto-muted">Unassigned</span>
              )}
            </Field>
            <Field icon={UserCheck} label="Assigned Sales Manager">
              {doctor.assigned_manager_user ? (
                <span>
                  <span className="font-semibold text-callisto-navy">
                    {doctor.assigned_manager_user.name}
                  </span>
                  <span className="block text-xs text-callisto-muted">
                    {doctor.assigned_manager_user.email}
                  </span>
                </span>
              ) : (
                <span className="text-callisto-muted">Unassigned</span>
              )}
            </Field>
            <Field icon={History} label="Created">
              <span className="text-callisto-muted" data-testid="doctor-created">
                {formatISTDate(doctor.created_at)}
              </span>
            </Field>
          </dl>
        </div>

        {/* Visit history */}
        <div className="bg-white border border-callisto rounded-xl p-5 md:p-7">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-4">
            Visit history
          </p>
          {visitsError ? (
            <div
              className="flex flex-col items-center text-center py-6"
              data-testid="doctor-visit-history-error"
            >
              <div className="h-12 w-12 rounded-lg bg-red-50 flex items-center justify-center mb-3">
                <TriangleAlert className="h-5 w-5 text-red-700" strokeWidth={1.75} />
              </div>
              <p className="text-sm font-semibold text-callisto-navy">
                Visit history could not be loaded
              </p>
              <p className="mt-1 text-xs text-callisto-muted max-w-xs">
                {visitsError} — this is not a record of "no visits".
              </p>
            </div>
          ) : visits.length === 0 ? (
            <div
              className="flex flex-col items-center text-center py-6"
              data-testid="doctor-visit-history-empty"
            >
              <div className="h-12 w-12 rounded-lg bg-callisto-navy/5 flex items-center justify-center mb-3">
                <Inbox className="h-5 w-5 text-callisto-navy" strokeWidth={1.75} />
              </div>
              <p className="text-sm font-semibold text-callisto-navy">
                No visits logged yet
              </p>
              <p className="mt-1 text-xs text-callisto-muted max-w-xs">
                Once a sales officer logs a visit with this doctor, the last 10
                will appear here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-callisto" data-testid="doctor-visit-history-list">
              {visits.map((v) => (
                <li key={v.id} className="py-3">
                  <Link
                    to={`/doctor-visits/${v.id}`}
                    className="flex items-start justify-between gap-2 group"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-callisto-navy truncate group-hover:underline">
                        {v.sales_officer_name}
                      </p>
                      <p className="text-[11px] text-callisto-muted flex items-center gap-1.5 flex-wrap">
                        <Clock size={11} />
                        {joinParts([formatISTDate(v.visit_date), formatISTTime(v.submitted_at)])}
                      </p>
                      {v.products_discussed?.length > 0 && (
                        <p className="mt-1 text-[11px] text-callisto-body truncate">
                          {joinParts(v.products_discussed.slice(0, 3))}
                        </p>
                      )}
                    </div>
                    <FeedbackBadge feedback={v.doctor_feedback} size="sm" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-callisto-muted flex items-center gap-1.5 mb-1">
        <Icon size={12} />
        {label}
      </dt>
      <dd className="text-sm text-callisto-body">{children}</dd>
    </div>
  );
}

function initials(name) {
  return (name || "?")
    .replace(/^Dr\.?\s*/i, "")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// RC-8: a private `formatDate` lived here and rendered in the VIEWER's zone
// with no label — one of the five date formats the audit found. Deleted; every
// date on this page now goes through `lib/datetime`, which pins Asia/Kolkata.
